import React, { useState, useEffect, useCallback } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Textarea } from '../components/ui/textarea';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../components/ui/dialog';
import axios from 'axios';
import { useAuth } from '../App';
import { API } from '../utils';
import { toast } from 'sonner';
import {
  ArrowLeft, Calendar, Clock, DollarSign,
  CheckCircle, XCircle, MessageCircle, Star, CreditCard, FileText, Upload,
  ArrowRight, Check
} from 'lucide-react';

// Progress Step Component
const ProgressSteps = ({ booking, userRole }) => {
  const getStepStatus = () => {
    const steps = [
      { key: 'booking', label: 'Booking', status: 'complete' },
      { key: 'agreement', label: 'Agreement', status: 'pending' },
      { key: 'payment', label: 'Payment', status: 'pending' },
      { key: 'deliverables', label: 'Deliverables', status: 'pending' },
      { key: 'complete', label: 'Complete', status: 'pending' }
    ];

    if (booking.agreement_id) {
      steps[1].status = 'complete';
    } else if (booking.status === 'pending') {
      steps[1].status = 'current';
    }

    if (booking.agreement?.business_signed && booking.agreement?.creative_signed) {
      steps[1].status = 'complete';
      if (booking.payment_status === 'unpaid') steps[2].status = 'current';
    }

    if (booking.payment_status === 'held' || booking.payment_status === 'released') {
      steps[2].status = 'complete';
      if (booking.status === 'in_progress') steps[3].status = 'current';
    }

    if (booking.status === 'deliverables_pending') steps[3].status = 'current';

    if (booking.status === 'completed') {
      steps[1].status = 'complete';
      steps[2].status = 'complete';
      steps[3].status = 'complete';
      steps[4].status = 'complete';
    }

    return steps;
  };

  const steps = getStepStatus();

  return (
    <div className="pb-2">
      <div className="flex items-center justify-between mb-4">
        {steps.map((step, idx) => (
          <React.Fragment key={step.key}>
            <div className="flex flex-col items-center flex-shrink-0">
              <div className={`w-6 h-6 md:w-8 md:h-8 rounded-full flex items-center justify-center text-[10px] md:text-xs font-medium transition-all ${
                step.status === 'complete' ? 'bg-[#0e0e0e] text-white' :
                step.status === 'current' ? 'bg-[#0e0e0e] text-white ring-2 md:ring-4 ring-[#e4e1db]' :
                'bg-[#f7f5f2] text-[#a0a0a0] border border-[#e4e1db]'
              }`}>
                {step.status === 'complete' ? <Check className="w-3 h-3 md:w-4 md:h-4" /> : idx + 1}
              </div>
              <span className={`text-[9px] md:text-xs mt-1 whitespace-nowrap ${
                step.status === 'current' ? 'text-[#0e0e0e] font-medium' :
                step.status === 'complete' ? 'text-[#5a5a5a]' : 'text-[#a0a0a0]'
              }`}>{step.label}</span>
            </div>
            {idx < steps.length - 1 && (
              <div className={`flex-1 h-0.5 mx-0.5 md:mx-2 min-w-[16px] ${
                steps[idx + 1].status === 'complete' || steps[idx + 1].status === 'current'
                  ? 'bg-[#0e0e0e]' : 'bg-[#e4e1db]'
              }`} />
            )}
          </React.Fragment>
        ))}
      </div>
    </div>
  );
};

const getStatusDisplay = (status, paymentStatus, agreement, userRole) => {
  if (status === 'agreement_pending' && agreement) {
    const businessSigned = agreement.business_signed;
    const creativeSigned = agreement.creative_signed;

    if (businessSigned && creativeSigned) {
      return { label: 'Ready for Payment', color: 'bg-[#f0fdf4] text-[#166534] border border-[#bbf7d0]', hint: 'Both parties have signed. Ready to proceed with payment.' };
    } else if (businessSigned && !creativeSigned) {
      return {
        label: userRole === 'creative' ? 'Your Signature Needed' : 'Awaiting Photographer',
        color: 'bg-[#fff7ed] text-[#9a3412] border border-[#fed7aa]',
        hint: userRole === 'creative' ? 'Please review and sign the agreement' : 'Waiting for photographer to sign'
      };
    } else if (!businessSigned && creativeSigned) {
      return {
        label: userRole === 'business' ? 'Your Acknowledgement Needed' : 'Awaiting Client',
        color: 'bg-[#fff7ed] text-[#9a3412] border border-[#fed7aa]',
        hint: userRole === 'business' ? 'Please review and sign the agreement' : 'Waiting for client to sign'
      };
    } else {
      return { label: 'Awaiting Acknowledgements', color: 'bg-[#fff7ed] text-[#9a3412] border border-[#fed7aa]', hint: 'Both parties need to acknowledge the agreement' };
    }
  }

  const statusMap = {
    'request':              { label: 'Pending Request',       color: 'bg-[#fefce8] text-[#854d0e] border border-[#fde68a]',  hint: 'Waiting for creative to respond' },
    'counter_proposed':     { label: 'Counter Proposal',      color: 'bg-[#eff6ff] text-[#1e40af] border border-[#bfdbfe]',  hint: 'Photographer has proposed changes' },
    'accepted':             { label: 'Accepted',              color: 'bg-[#f0fdf4] text-[#166534] border border-[#bbf7d0]',  hint: 'Request accepted - create agreement to proceed' },
    'declined':             { label: 'Declined',              color: 'bg-[#fef2f2] text-[#991b1b] border border-[#fecaca]',  hint: 'Request was declined' },
    'withdrawn':            { label: 'Withdrawn',             color: 'bg-[#f3f4f6] text-[#374151] border border-[#e5e7eb]',  hint: 'Booking was withdrawn by client' },
    'pending':              { label: 'Needs Agreement',       color: 'bg-[#f5ead0] text-[#8a6a1a] border border-[#e8d49a]',  hint: 'Create and sign an agreement to proceed' },
    'agreement_pending':    { label: 'Awaiting Acknowledgements', color: 'bg-[#fff7ed] text-[#9a3412] border border-[#fed7aa]', hint: 'Both parties need to acknowledge the agreement' },
    'confirmed':            { label: 'Confirmed',             color: 'bg-[#eff6ff] text-[#1e40af] border border-[#bfdbfe]',  hint: 'Booking is confirmed' },
    'in_progress':          { label: 'Work in Progress',      color: 'bg-[#fdf4ff] text-[#7e22ce] border border-[#e9d5ff]',  hint: 'Photographer is working on deliverables' },
    'deliverables_pending': { label: 'Ready for Review',      color: 'bg-[#f5ead0] text-[#8a6a1a] border border-[#e8d49a]',  hint: 'Deliverables submitted for review' },
    'completed':            { label: 'Completed',             color: 'bg-[#f0fdf4] text-[#166534] border border-[#bbf7d0]',  hint: 'Project successfully completed' },
    'cancelled':            { label: 'Cancelled',             color: 'bg-[#fef2f2] text-[#991b1b] border border-[#fecaca]',  hint: 'Booking was cancelled' }
  };
  return statusMap[status] || { label: status, color: 'bg-[#f3f4f6] text-[#374151] border border-[#e5e7eb]', hint: '' };
};

const Bookings = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { user, token } = useAuth();
  const [bookings, setBookings] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedBooking, setSelectedBooking] = useState(null);
  const [reviewOpen, setReviewOpen] = useState(false);
  const [reviewData, setReviewData] = useState({ rating: 5, comment: '' });
  const [actionLoading, setActionLoading] = useState(false);
  const [activeTab, setActiveTab] = useState('active');
  const [counterOpen, setCounterOpen] = useState(false);
  const [counterData, setCounterData] = useState({ proposed_rate: '', proposed_date: '', proposed_delivery_date: '', proposed_time: '', note: '' });
  const [profileChecklist, setProfileChecklist] = useState(null);
  const [stripeEnabled, setStripeEnabled] = useState(false);

  useEffect(() => {
    const tabParam = searchParams.get('tab');
    if (tabParam && ['requests', 'active', 'completed'].includes(tabParam)) {
      setActiveTab(tabParam);
    }
  }, [searchParams]);

  const config = { withCredentials: true, headers: token ? { Authorization: `Bearer ${token}` } : {} };

  const requestStatuses = ['request', 'counter_proposed'];
  const activeStatuses = ['accepted', 'pending', 'agreement_pending', 'confirmed', 'in_progress', 'deliverables_pending'];
  const completedStatuses = ['completed', 'cancelled', 'declined', 'withdrawn'];

  const requests = bookings.filter(b => requestStatuses.includes(b.status));
  const activeBookings = bookings.filter(b => activeStatuses.includes(b.status));
  const completedBookings = bookings.filter(b => completedStatuses.includes(b.status));

  const fetchBookings = useCallback(async () => {
    try {
      const response = await axios.get(`${API}/bookings`, config);
      setBookings(response.data);
    } catch (error) {
      toast.error('Failed to fetch bookings');
    } finally {
      setLoading(false);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token]);

  const fetchProfileStatus = useCallback(async () => {
    if (user?.role !== 'creative') return;
    try {
      const response = await axios.get(`${API}/photographers/user/${user.user_id}`, config);
      const profile = response.data;
      setProfileChecklist({
        hasTitle: !!profile.title && profile.title.length > 0,
        hasLocation: !!profile.city && profile.city.length > 0,
        hasRate: profile.hourly_rate > 0,
        hasSpecialties: profile.specialties?.length > 0,
        hasPortfolio: profile.portfolio_items?.length > 0,
        hasAvailability: (profile.recurring_unavailable_days?.length > 0) || (profile.availability?.length > 0) || profile.availability_set === true,
        hasProfilePicture: !!profile.user_picture
      });
    } catch (error) {
      console.error('Error fetching profile status:', error);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user?.user_id, user?.role, token]);

  const handleConnectStripe = async () => {
    try {
      await axios.post(`${API}/connect/create-account`, {}, config);
      const response = await axios.post(`${API}/connect/onboarding-link`, { origin_url: window.location.origin }, config);
      window.location.href = response.data.url;
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to connect Stripe');
    }
  };

  const handleAcceptRequest = async (bookingId) => {
    setActionLoading(true);
    try {
      await axios.post(`${API}/bookings/${bookingId}/accept`, {}, config);
      toast.success('Request accepted!');
      fetchBookings();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to accept');
    } finally {
      setActionLoading(false);
    }
  };

  const handleRejectCounter = async (bookingId) => {
    setActionLoading(true);
    try {
      await axios.post(`${API}/bookings/${bookingId}/reject-counter`, {}, config);
      toast.success('Counter proposal declined — booking reverted to original request');
      fetchBookings();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to decline counter proposal');
    } finally {
      setActionLoading(false);
    }
  };

  const handleDeclineRequest = async (bookingId, reason = '') => {
    setActionLoading(true);
    try {
      await axios.post(`${API}/bookings/${bookingId}/decline`, { reason }, config);
      toast.success('Request declined');
      fetchBookings();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to decline');
    } finally {
      setActionLoading(false);
    }
  };

  const handleCounterPropose = async () => {
    if (!selectedBooking) return;
    setActionLoading(true);
    try {
      await axios.post(`${API}/bookings/${selectedBooking.booking_id}/counter`, counterData, config);
      toast.success('Counter proposal sent!');
      setCounterOpen(false);
      setCounterData({ proposed_rate: '', proposed_date: '', proposed_delivery_date: '', proposed_time: '', note: '' });
      fetchBookings();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to send proposal');
    } finally {
      setActionLoading(false);
    }
  };

  const handleAcceptCounter = async (bookingId) => {
    setActionLoading(true);
    try {
      await axios.post(`${API}/bookings/${bookingId}/accept-counter`, {}, config);
      toast.success('Counter proposal accepted!');
      fetchBookings();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to accept');
    } finally {
      setActionLoading(false);
    }
  };

  const pollPaymentStatus = useCallback(async (sessionId, attempts = 0) => {
    const maxAttempts = 5;
    if (attempts >= maxAttempts) { toast.info('Payment status check timed out. Please refresh the page.'); return; }
    try {
      const response = await axios.get(`${API}/payments/status/${sessionId}`, { withCredentials: true, headers: token ? { Authorization: `Bearer ${token}` } : {} });
      if (response.data.payment_status === 'paid') {
        toast.success('Payment successful!');
        fetchBookings();
        window.history.replaceState({}, '', '/bookings');
        return;
      } else if (response.data.status === 'expired') {
        toast.error('Payment session expired');
        return;
      }
      setTimeout(() => pollPaymentStatus(sessionId, attempts + 1), 2000);
    } catch (error) {
      console.error('Error checking payment:', error);
    }
  }, [token, fetchBookings]);

  const handleWithdrawBooking = async (bookingId) => {
    if (!window.confirm('Are you sure you want to withdraw this booking request?')) return;
    setActionLoading(true);
    try {
      await axios.post(`${API}/bookings/${bookingId}/withdraw`, {}, config);
      toast.success('Booking withdrawn');
      fetchBookings();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to withdraw booking');
    } finally {
      setActionLoading(false);
    }
  };

  useEffect(() => {
    fetchBookings();
    fetchProfileStatus();
    if (user?.role === 'creative') {
      axios.get(`${API}/connect/status`, config)
        .then(res => setStripeEnabled(res.data?.connected === true))
        .catch(() => setStripeEnabled(true));
    }
    const sessionId = searchParams.get('session_id');
    if (sessionId) pollPaymentStatus(sessionId);
    const highlightId = searchParams.get('highlight');
    if (highlightId) {
      setTimeout(() => {
        const el = document.querySelector(`[data-testid="booking-${highlightId}"]`);
        if (el) { el.scrollIntoView({ behavior: 'smooth', block: 'center' }); el.classList.add('ring-2', 'ring-[#d4a843]'); setTimeout(() => el.classList.remove('ring-2', 'ring-[#d4a843]'), 3000); }
      }, 800);
    }
    const interval = setInterval(fetchBookings, 15000);
    return () => clearInterval(interval);
  }, [fetchBookings, fetchProfileStatus, pollPaymentStatus, searchParams]);

  const handlePayment = async (booking) => {
    setActionLoading(true);
    try {
      const response = await axios.post(`${API}/payments/checkout`, {
        booking_id: booking.booking_id,
        origin_url: window.location.origin
      }, { withCredentials: true, headers: token ? { Authorization: `Bearer ${token}` } : {} });
      window.location.href = response.data.url;
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Payment failed');
      setActionLoading(false);
    }
  };

  const updateStatus = async (bookingId, status) => {
    setActionLoading(true);
    try {
      await axios.put(`${API}/bookings/${bookingId}/status`, { status }, { withCredentials: true, headers: token ? { Authorization: `Bearer ${token}` } : {} });
      toast.success(`Booking ${status}`);
      fetchBookings();
      setSelectedBooking(null);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Update failed');
    } finally {
      setActionLoading(false);
    }
  };

  const submitReview = async () => {
    if (!selectedBooking || !reviewData.comment.trim()) { toast.error('Please add a comment'); return; }
    setActionLoading(true);
    try {
      await axios.post(`${API}/reviews`, { booking_id: selectedBooking.booking_id, rating: reviewData.rating, comment: reviewData.comment }, { withCredentials: true, headers: token ? { Authorization: `Bearer ${token}` } : {} });
      toast.success('Review submitted!');
      setReviewOpen(false);
      setReviewData({ rating: 5, comment: '' });
      fetchBookings();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Review failed');
    } finally {
      setActionLoading(false);
    }
  };

  const getPaymentDisplay = (status) => {
    const paymentMap = {
      'paid':     { label: 'Paid',       color: 'bg-[#f0fdf4] text-[#166534] border border-[#bbf7d0]' },
      'held':     { label: 'In Escrow',  color: 'bg-[#eff6ff] text-[#1e40af] border border-[#bfdbfe]' },
      'released': { label: 'Released',   color: 'bg-[#f0fdf4] text-[#166534] border border-[#bbf7d0]' },
      'unpaid':   { label: 'Unpaid',     color: 'bg-[#fefce8] text-[#854d0e] border border-[#fde68a]' },
      'refunded': { label: 'Refunded',   color: 'bg-[#fef2f2] text-[#991b1b] border border-[#fecaca]' }
    };
    return paymentMap[status] || { label: status, color: 'bg-[#f3f4f6] text-[#374151] border border-[#e5e7eb]' };
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-[#f7f5f2]">
        <div className="animate-pulse text-[#a0a0a0] text-sm">Loading bookings...</div>
      </div>
    );
  }

  const renderBookingCard = (booking) => {
    const statusDisplay = getStatusDisplay(booking.status, booking.payment_status, booking.agreement, user?.role);
    const paymentDisplay = getPaymentDisplay(booking.payment_status);
    const initials = (user?.role === 'business' ? booking.creative_name : booking.business_name)
      ?.split(' ').map(w => w[0]).join('').slice(0, 2).toUpperCase() || '?';

    return (
      <div
        key={booking.booking_id}
        className="bg-white border border-[#e4e1db] rounded-[14px] overflow-hidden hover:border-[#0e0e0e] transition-colors"
        data-testid={`booking-${booking.booking_id}`}
      >
        {/* Progress Steps */}
        {!['request', 'counter_proposed', 'declined'].includes(booking.status) && (
          <div className="bg-[#f7f5f2] px-5 pt-4 pb-3 border-b border-[#e4e1db]">
            <ProgressSteps booking={booking} userRole={user?.role} />
          </div>
        )}

        <div className="p-5">
          <div className="flex flex-col md:flex-row md:items-start justify-between gap-4">
            <div className="flex-1">
              {/* Title row */}
              <div className="flex items-center gap-3 mb-2 flex-wrap">
                <div className="w-[38px] h-[38px] rounded-full bg-[#f7f5f2] border border-[#e4e1db] flex items-center justify-center text-[13px] font-semibold text-[#0e0e0e] flex-shrink-0">
                  {initials}
                </div>
                <div>
                  <h3 className="text-[13px] font-semibold text-[#0e0e0e]">{booking.project_title}</h3>
                  <p className="text-[11px] text-[#a0a0a0] mt-0.5">
                    {user?.role === 'business' ? booking.creative_name : booking.business_name}
                  </p>
                </div>
                <div className="flex items-center gap-2 flex-wrap ml-auto">
                  <span className={`text-[10px] font-bold px-2.5 py-1 rounded-full tracking-[0.3px] ${statusDisplay.color}`}>
                    {statusDisplay.label}
                  </span>
                  {!['request', 'counter_proposed', 'declined'].includes(booking.status) && (
                    <span className={`text-[10px] font-bold px-2.5 py-1 rounded-full tracking-[0.3px] ${paymentDisplay.color}`}>
                      {paymentDisplay.label}
                    </span>
                  )}
                  <span className="text-[14px] font-bold text-[#0e0e0e]">${parseFloat(booking.total_amount || 0).toFixed(2)}</span>
                </div>
              </div>

              {/* Status hint */}
              {statusDisplay.hint && booking.status !== 'completed' && (
                <p className="text-[12px] text-[#5a5a5a] mb-3 flex items-center gap-1.5 ml-[50px]">
                  <ArrowRight className="w-3.5 h-3.5 flex-shrink-0" />
                  {booking.status === 'accepted' && !booking.agreement_id && user?.role === 'business'
                    ? 'Pending agreement creation by photographer'
                    : statusDisplay.hint}
                </p>
              )}

              {/* Date/time/location */}
              <div className="flex flex-wrap items-center gap-3 text-[12px] text-[#a0a0a0] mb-3 ml-[50px]">
                <span className="flex items-center gap-1"><Calendar className="w-3.5 h-3.5" />Shoot: {booking.date}</span>
                <span className="flex items-center gap-1"><Clock className="w-3.5 h-3.5" />{booking.time}</span>
                {booking.delivery_date && <span className="flex items-center gap-1"><Calendar className="w-3.5 h-3.5" />Delivery: {booking.delivery_date}</span>}
                {!['request', 'counter_proposed'].includes(booking.status) && booking.location && (
                  <span>📍 {booking.location}</span>
                )}
              </div>

              {/* Project details for requests */}
              {['request', 'counter_proposed'].includes(booking.status) && (
                <div className="p-4 bg-[#f7f5f2] rounded-[14px] border border-[#e4e1db] mb-3 ml-[50px]">
                  <p className="text-[12px] font-semibold text-[#0e0e0e] mb-2">Project Details</p>
                  {booking.project_description && <p className="text-[12px] text-[#5a5a5a] mb-2">{booking.project_description}</p>}
                  {booking.deliverables && (
                    <div className="mt-2">
                      <p className="text-[11px] font-semibold text-[#a0a0a0] uppercase tracking-wide">Expected Deliverables</p>
                      <p className="text-[12px] text-[#5a5a5a]">{booking.deliverables}</p>
                    </div>
                  )}
                  {booking.location && (
                    <div className="mt-2">
                      <p className="text-[11px] font-semibold text-[#a0a0a0] uppercase tracking-wide">Location</p>
                      <p className="text-[12px] text-[#5a5a5a]">{booking.location}</p>
                    </div>
                  )}
                  <div className="mt-2 grid grid-cols-2 gap-2">
                    <div>
                      <p className="text-[11px] font-semibold text-[#a0a0a0] uppercase tracking-wide">Shoot Date</p>
                      <p className="text-[12px] text-[#5a5a5a]">{booking.date} {booking.time && `@ ${booking.time}`}</p>
                    </div>
                    {booking.delivery_date && (
                      <div>
                        <p className="text-[11px] font-semibold text-[#a0a0a0] uppercase tracking-wide">Delivery Date</p>
                        <p className="text-[12px] text-[#5a5a5a]">{booking.delivery_date}</p>
                      </div>
                    )}
                  </div>
                  {booking.duration_hours && (
                    <div className="mt-2">
                      <p className="text-[11px] font-semibold text-[#a0a0a0] uppercase tracking-wide">Duration</p>
                      <p className="text-[12px] text-[#5a5a5a]">{booking.duration_hours} hour{booking.duration_hours > 1 ? 's' : ''}</p>
                    </div>
                  )}
                </div>
              )}

              {/* Counter proposal details */}
              {booking.status === 'counter_proposed' && booking.counter_proposal && (
                <div className="p-3 bg-[#eff6ff] rounded-[14px] border border-[#bfdbfe] mb-3 ml-[50px]">
                  <p className="text-[12px] font-semibold text-[#1e40af] mb-2">Counter Proposal from Photographer</p>
                  {booking.counter_proposal.proposed_rate && <p className="text-[12px] text-[#1e40af]">New Rate: ${booking.counter_proposal.proposed_rate}</p>}
                  {booking.counter_proposal.proposed_date && <p className="text-[12px] text-[#1e40af]">Shoot Date: {booking.counter_proposal.proposed_date}</p>}
                  {booking.counter_proposal.proposed_time && <p className="text-[12px] text-[#1e40af]">Shoot Time: {booking.counter_proposal.proposed_time}</p>}
                  {booking.counter_proposal.proposed_delivery_date && <p className="text-[12px] text-[#1e40af]">Delivery Date: {booking.counter_proposal.proposed_delivery_date}</p>}
                  {booking.counter_proposal.note && <p className="text-[12px] text-[#1e40af] mt-1">&ldquo;{booking.counter_proposal.note}&rdquo;</p>}
                </div>
              )}
            </div>

            {/* Actions */}
            <div className="flex flex-col gap-2 flex-shrink-0">
              {/* Creative: request actions */}
              {user?.role === 'creative' && booking.status === 'request' && (
                <>
                  <Link to="/messages" state={{ partnerId: booking.business_id }}>
                    <button className="w-full text-[13px] font-medium px-4 py-2 rounded-full border border-[#e4e1db] bg-white text-[#0e0e0e] hover:border-[#0e0e0e] transition-all flex items-center gap-2 justify-center">
                      <MessageCircle className="w-4 h-4" /> Message Client
                    </button>
                  </Link>
                  {stripeEnabled ? (
                    <button onClick={() => handleAcceptRequest(booking.booking_id)} disabled={actionLoading} className="text-[13px] font-medium px-4 py-2 rounded-full bg-[#0e0e0e] text-white hover:opacity-85 transition-opacity flex items-center gap-2 justify-center" data-testid="accept-request-btn">
                      <CheckCircle className="w-4 h-4" /> Accept
                    </button>
                  ) : (
                    <div className="space-y-1">
                      <button disabled className="w-full text-[13px] font-medium px-4 py-2 rounded-full bg-[#e4e1db] text-[#a0a0a0] cursor-not-allowed flex items-center gap-2 justify-center">
                        <CreditCard className="w-4 h-4" /> Accept
                      </button>
                      <p className="text-[11px] text-[#f87171] text-center">
                        <button onClick={handleConnectStripe} className="underline font-medium">Connect Stripe</button> to accept
                      </p>
                    </div>
                  )}
                  <button onClick={() => { setSelectedBooking(booking); setCounterData({ proposed_rate: booking.total_amount || '', proposed_date: booking.date || '', proposed_delivery_date: booking.delivery_date || '', proposed_time: booking.time || '', note: '' }); setCounterOpen(true); }} className="w-full text-[13px] font-medium px-4 py-2 rounded-full border border-[#bfdbfe] text-[#1e40af] hover:bg-[#eff6ff] transition-all flex items-center gap-2 justify-center" data-testid="counter-propose-btn">
                    Propose Changes
                  </button>
                  <button onClick={() => handleDeclineRequest(booking.booking_id)} disabled={actionLoading} className="text-[13px] font-medium px-4 py-2 rounded-full border border-[#fecaca] text-[#991b1b] hover:bg-[#fef2f2] transition-all flex items-center gap-2 justify-center" data-testid="decline-request-btn">
                    <XCircle className="w-4 h-4" /> Decline
                  </button>
                </>
              )}

              {/* Business: counter proposal actions */}
              {user?.role === 'business' && booking.status === 'counter_proposed' && (
                <>
                  <button onClick={() => handleAcceptCounter(booking.booking_id)} disabled={actionLoading} className="text-[13px] font-medium px-4 py-2 rounded-full bg-[#0e0e0e] text-white hover:opacity-85 transition-opacity flex items-center gap-2 justify-center" data-testid="accept-counter-btn">
                    <CheckCircle className="w-4 h-4" /> Accept Proposal
                  </button>
                  <button onClick={() => handleRejectCounter(booking.booking_id)} disabled={actionLoading} className="w-full text-[13px] font-medium px-4 py-2 rounded-full border border-[#fecaca] text-[#991b1b] hover:bg-[#fef2f2] transition-all flex items-center gap-2 justify-center">
                    Decline
                  </button>
                </>
              )}

              {/* Business: withdraw request */}
              {user?.role === 'business' && booking.status === 'request' && (
                <button onClick={() => handleWithdrawBooking(booking.booking_id)} disabled={actionLoading} className="text-[13px] font-medium px-4 py-2 rounded-full border border-[#fecaca] text-[#991b1b] hover:bg-[#fef2f2] transition-all flex items-center gap-2 justify-center" data-testid="withdraw-request-btn">
                  <XCircle className="w-4 h-4" /> Withdraw Request
                </button>
              )}

              {/* Business: withdraw before agreement */}
              {user?.role === 'business' && ['accepted', 'pending'].includes(booking.status) && !booking.agreement_id && (
                <button onClick={() => handleWithdrawBooking(booking.booking_id)} disabled={actionLoading} className="text-[13px] font-medium px-4 py-2 rounded-full border border-[#fecaca] text-[#991b1b] hover:bg-[#fef2f2] transition-all flex items-center gap-2 justify-center">
                  <XCircle className="w-4 h-4" /> Withdraw
                </button>
              )}

              {/* Business: agreement actions */}
              {user?.role === 'business' && booking.agreement_id && booking.payment_status === 'unpaid' && (
                <div className="flex flex-col gap-2">
                  <Link to={`/bookings/${booking.booking_id}/agreement`}>
                    <button className="w-full text-[13px] font-medium px-4 py-2 rounded-full bg-[#0e0e0e] text-white hover:opacity-85 transition-opacity flex items-center gap-2 justify-center" data-testid="view-agreement-btn">
                      <FileText className="w-4 h-4" />
                      {booking.agreement?.business_signed ? 'View Agreement' : booking.agreement?.creative_signed ? 'Acknowledge Agreement' : 'Waiting for Photographer'}
                    </button>
                  </Link>
                  {!booking.agreement?.business_signed && !booking.agreement?.creative_signed && (
                    <button onClick={() => handleWithdrawBooking(booking.booking_id)} disabled={actionLoading} className="text-[13px] font-medium px-4 py-2 rounded-full border border-[#fecaca] text-[#991b1b] hover:bg-[#fef2f2] transition-all flex items-center gap-2 justify-center">
                      <XCircle className="w-4 h-4" /> Withdraw
                    </button>
                  )}
                </div>
              )}

              {user?.role === 'business' && booking.status === 'in_progress' && (
                <Link to={`/deliverables/${booking.booking_id}`}>
                  <button className="w-full text-[13px] font-medium px-4 py-2 rounded-full bg-[#0e0e0e] text-white hover:opacity-85 transition-opacity flex items-center gap-2 justify-center" data-testid="view-deliverables-btn">
                    <Upload className="w-4 h-4" /> View Deliverables
                  </button>
                </Link>
              )}
              {user?.role === 'business' && booking.status === 'deliverables_pending' && (
                <Link to={`/deliverables/${booking.booking_id}`}>
                  <button className="w-full text-[13px] font-medium px-4 py-2 rounded-full bg-[#0e0e0e] text-white hover:opacity-85 transition-opacity flex items-center gap-2 justify-center" data-testid="review-deliverables-btn">
                    <CheckCircle className="w-4 h-4" /> Review Deliverables
                  </button>
                </Link>
              )}
              {user?.role === 'business' && booking.status === 'completed' && !booking.reviewed && (
                <button onClick={() => { setSelectedBooking(booking); setReviewOpen(true); }} className="text-[13px] font-medium px-4 py-2 rounded-full border border-[#e4e1db] bg-white text-[#0e0e0e] hover:border-[#0e0e0e] transition-all flex items-center gap-2 justify-center" data-testid="leave-review-btn">
                  <Star className="w-4 h-4" /> Leave Review
                </button>
              )}
              {user?.role === 'business' && booking.status === 'completed' && (
                <Link to={`/deliverables/${booking.booking_id}`}>
                  <button className="w-full text-[13px] font-medium px-4 py-2 rounded-full border border-[#e4e1db] bg-white text-[#5a5a5a] hover:border-[#0e0e0e] hover:text-[#0e0e0e] transition-all flex items-center gap-2 justify-center">
                    <Upload className="w-4 h-4" /> View Deliverables
                  </button>
                </Link>
              )}

              {/* Creative: create agreement */}
              {user?.role === 'creative' && ['accepted', 'pending'].includes(booking.status) && !booking.agreement_id && (
                <div className="flex flex-col gap-2">
                  <Link to={`/bookings/${booking.booking_id}/agreement`}>
                    <button className="w-full text-[13px] font-medium px-4 py-2 rounded-full border border-[#e4e1db] bg-white text-[#0e0e0e] hover:border-[#0e0e0e] transition-all flex items-center gap-2 justify-center">
                      <FileText className="w-4 h-4" /> Create Agreement
                    </button>
                  </Link>
                  <button onClick={async () => {
                    if (!window.confirm('Are you sure you want to withdraw from this booking?')) return;
                    try {
                      await axios.post(`${API}/bookings/${booking.booking_id}/creative-withdraw`, {}, config);
                      toast.success('Booking withdrawn'); fetchBookings();
                    } catch (error) { toast.error(error.response?.data?.detail || 'Failed to withdraw booking'); }
                  }} disabled={actionLoading} className="text-[13px] font-medium px-4 py-2 rounded-full border border-[#fecaca] text-[#991b1b] hover:bg-[#fef2f2] transition-all flex items-center gap-2 justify-center">
                    <XCircle className="w-4 h-4" /> Withdraw
                  </button>
                </div>
              )}

              {/* Creative: agreement signed */}
              {user?.role === 'creative' && booking.agreement_id && ['accepted', 'pending', 'agreement_pending'].includes(booking.status) && (
                <div className="flex flex-col gap-2">
                  <Link to={`/bookings/${booking.booking_id}/agreement`}>
                    <button className="w-full text-[13px] font-medium px-4 py-2 rounded-full bg-[#0e0e0e] text-white hover:opacity-85 transition-opacity flex items-center gap-2 justify-center">
                      <FileText className="w-4 h-4" />
                      {booking.agreement?.creative_signed ? 'View Agreement' : 'Acknowledge Agreement'}
                    </button>
                  </Link>
                  {!booking.agreement?.business_signed && !booking.agreement?.creative_signed && (
                    <button onClick={async () => {
                      if (!window.confirm('Are you sure you want to withdraw from this booking?')) return;
                      try {
                        await axios.post(`${API}/bookings/${booking.booking_id}/creative-withdraw`, {}, config);
                        toast.success('Booking withdrawn'); fetchBookings();
                      } catch (error) { toast.error(error.response?.data?.detail || 'Failed to withdraw booking'); }
                    }} disabled={actionLoading} className="text-[13px] font-medium px-4 py-2 rounded-full border border-[#fecaca] text-[#991b1b] hover:bg-[#fef2f2] transition-all flex items-center gap-2 justify-center">
                      <XCircle className="w-4 h-4" /> Withdraw
                    </button>
                  )}
                </div>
              )}

              {user?.role === 'creative' && booking.status === 'in_progress' && (
                <Link to={`/deliverables/${booking.booking_id}`}>
                  <button className="w-full text-[13px] font-medium px-4 py-2 rounded-full bg-[#0e0e0e] text-white hover:opacity-85 transition-opacity flex items-center gap-2 justify-center" data-testid="upload-deliverables-btn">
                    <Upload className="w-4 h-4" /> Upload Deliverables
                  </button>
                </Link>
              )}
              {user?.role === 'creative' && booking.status === 'deliverables_pending' && (
                <Link to={`/deliverables/${booking.booking_id}`}>
                  <button className="w-full text-[13px] font-medium px-4 py-2 rounded-full border border-[#e4e1db] bg-white text-[#5a5a5a] flex items-center gap-2 justify-center">
                    <Clock className="w-4 h-4" /> Awaiting Review
                  </button>
                </Link>
              )}
              {user?.role === 'creative' && booking.status === 'completed' && (
                <Link to={`/deliverables/${booking.booking_id}`}>
                  <button className="w-full text-[13px] font-medium px-4 py-2 rounded-full border border-[#e4e1db] bg-white text-[#5a5a5a] hover:border-[#0e0e0e] hover:text-[#0e0e0e] transition-all flex items-center gap-2 justify-center">
                    <Upload className="w-4 h-4" /> View Deliverables
                  </button>
                </Link>
              )}

              {/* Message button */}
              {!['request', 'counter_proposed', 'declined'].includes(booking.status) && (
                <Link to="/messages" state={{ partnerId: user?.role === 'business' ? booking.creative_id : booking.business_id }}>
                  <button className="w-full text-[13px] font-medium px-4 py-2 rounded-full border border-[#e4e1db] bg-white text-[#0e0e0e] hover:border-[#0e0e0e] transition-all flex items-center gap-2 justify-center">
                    <MessageCircle className="w-4 h-4" /> Message
                  </button>
                </Link>
              )}
            </div>
          </div>
        </div>
      </div>
    );
  };

  return (
    <div className="min-h-screen bg-[#f7f5f2]">
      {/* Header */}
      <div className="bg-white border-b border-[#e4e1db] px-5 md:px-10 pt-7 pb-5">
        <p className="text-[10px] font-bold tracking-[1.8px] uppercase text-[#a0a0a0] mb-1.5">Manage</p>
        <h1 className="font-display font-normal text-[28px] md:text-[32px] text-[#0e0e0e] tracking-[-0.5px] leading-[1.1]">Bookings</h1>
      </div>

      {/* Content */}
      <div className="px-5 md:px-10 py-6 space-y-4">
        {/* Tabs */}
        <div className="flex gap-1.5 flex-wrap">
          {(user?.role === 'creative' || user?.role === 'business') && requests.length > 0 && (
            <button
              onClick={() => setActiveTab('requests')}
              className={`text-[13px] font-medium px-5 py-2 rounded-full border transition-all ${
                activeTab === 'requests'
                  ? 'bg-[#0e0e0e] text-white border-[#0e0e0e]'
                  : 'bg-white text-[#5a5a5a] border-[#e4e1db] hover:border-[#0e0e0e] hover:text-[#0e0e0e]'
              }`}
            >
              {user?.role === 'creative' ? 'Requests' : 'Pending'} ({requests.length})
            </button>
          )}
          <button
            onClick={() => setActiveTab('active')}
            className={`text-[13px] font-medium px-5 py-2 rounded-full border transition-all ${
              activeTab === 'active'
                ? 'bg-[#0e0e0e] text-white border-[#0e0e0e]'
                : 'bg-white text-[#5a5a5a] border-[#e4e1db] hover:border-[#0e0e0e] hover:text-[#0e0e0e]'
            }`}
          >
            Active ({activeBookings.length})
          </button>
          <button
            onClick={() => setActiveTab('completed')}
            className={`text-[13px] font-medium px-5 py-2 rounded-full border transition-all ${
              activeTab === 'completed'
                ? 'bg-[#0e0e0e] text-white border-[#0e0e0e]'
                : 'bg-white text-[#5a5a5a] border-[#e4e1db] hover:border-[#0e0e0e] hover:text-[#0e0e0e]'
            }`}
          >
            Completed ({completedBookings.length})
          </button>
        </div>
        {activeTab === 'requests' && (
          requests.length > 0 ? requests.map(renderBookingCard) : (
            <EmptyState icon={<CalendarIcon />} title="No pending requests" />
          )
        )}

        {activeTab === 'active' && (
          activeBookings.length > 0 ? activeBookings.map(renderBookingCard) : (
            <div className="bg-white border border-[#e4e1db] rounded-[24px] p-10 text-center">
              <div className="opacity-20 mb-4 flex justify-center">
                <CalendarIcon />
              </div>
              <p className="text-[15px] font-semibold text-[#0e0e0e] mb-1.5">No active bookings</p>
              {user?.role === 'business' ? (
                <>
                  <p className="text-[13px] text-[#5a5a5a] mb-5">Find creatives and make your first booking</p>
                  <Link to="/photographers" className="inline-flex items-center text-[13px] font-medium px-5 py-2 rounded-full bg-[#0e0e0e] text-white hover:opacity-85 transition-opacity">
                    Browse Photographers
                  </Link>
                </>
              ) : profileChecklist ? (
                <div className="max-w-md mx-auto text-left mt-4">
                  <p className="text-[13px] text-[#5a5a5a] mb-4 text-center">Complete your profile to attract more clients</p>
                  <div className="mb-4">
                    <div className="flex justify-between text-[12px] mb-1.5">
                      <span className="text-[#5a5a5a]">Profile completion</span>
                      <span className="font-semibold text-[#0e0e0e]">{Object.values(profileChecklist).filter(Boolean).length}/{Object.keys(profileChecklist).length}</span>
                    </div>
                    <div className="h-1 bg-[#e4e1db] rounded-full overflow-hidden">
                      <div className="h-full bg-[#0e0e0e] rounded-full transition-all" style={{ width: `${(Object.values(profileChecklist).filter(Boolean).length / Object.keys(profileChecklist).length) * 100}%` }} />
                    </div>
                  </div>
                  <div className="space-y-2 mb-5">
                    {[
                      { key: 'hasProfilePicture', label: 'Upload a profile photo', link: '/edit-profile' },
                      { key: 'hasTitle', label: 'Add your professional title', link: '/edit-profile' },
                      { key: 'hasLocation', label: 'Set your location', link: '/edit-profile' },
                      { key: 'hasRate', label: 'Set your hourly rate', link: '/edit-profile' },
                      { key: 'hasSpecialties', label: 'Add your specialties', link: '/edit-profile' },
                      { key: 'hasPortfolio', label: 'Add portfolio items', link: '/edit-profile' },
                      { key: 'hasAvailability', label: 'Set your availability', link: '/availability' }
                    ].map(item => (
                      <Link key={item.key} to={item.link} className={`flex items-center gap-3 p-3 rounded-[14px] border transition-colors ${profileChecklist[item.key] ? 'bg-[#f0fdf4] border-[#bbf7d0] text-[#166534]' : 'bg-[#f7f5f2] border-[#e4e1db] text-[#5a5a5a] hover:border-[#0e0e0e]'}`}>
                        {profileChecklist[item.key]
                          ? <CheckCircle className="w-4 h-4 text-[#166534] flex-shrink-0" />
                          : <div className="w-4 h-4 rounded-full border-2 border-[#c8c3bb] flex-shrink-0" />}
                        <span className="text-[13px]">{item.label}</span>
                        {!profileChecklist[item.key] && <ArrowRight className="w-3.5 h-3.5 ml-auto text-[#a0a0a0]" />}
                      </Link>
                    ))}
                  </div>
                  {Object.values(profileChecklist).every(Boolean) && (
                    <p className="text-[#166534] text-center text-[13px] font-medium">Your profile is complete! Clients can now find and book you.</p>
                  )}
                </div>
              ) : (
                <>
                  <p className="text-[13px] text-[#5a5a5a] mb-5">Complete your profile to start receiving bookings</p>
                  <Link to="/edit-profile" className="inline-flex items-center text-[13px] font-medium px-5 py-2 rounded-full bg-[#0e0e0e] text-white hover:opacity-85 transition-opacity">
                    Complete Profile
                  </Link>
                </>
              )}
            </div>
          )
        )}

        {activeTab === 'completed' && (
          completedBookings.length > 0 ? completedBookings.map(renderBookingCard) : (
            <EmptyState icon={<CheckCircle className="w-10 h-10" />} title="No completed bookings yet" />
          )
        )}
      </div>

      {/* Counter Proposal Dialog */}
      <Dialog open={counterOpen} onOpenChange={setCounterOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle className="font-display font-normal text-[24px]">Propose Changes</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 mt-4">
            <p className="text-[13px] text-[#5a5a5a]">Suggest alternative terms for this booking request.</p>
            <div>
              <label className="text-[12px] font-semibold text-[#0e0e0e] mb-1.5 block">Proposed Rate ($)</label>
              <input type="number" value={counterData.proposed_rate} onChange={e => setCounterData({ ...counterData, proposed_rate: e.target.value })} placeholder={selectedBooking?.total_amount?.toString()} className="w-full px-4 py-2.5 border border-[#e4e1db] rounded-[14px] text-[14px] focus:border-[#0e0e0e] focus:outline-none focus:ring-[3px] focus:ring-[rgba(14,14,14,0.06)]" />
            </div>
            <div>
              <label className="text-[12px] font-semibold text-[#0e0e0e] mb-1.5 block">Proposed Shoot Date</label>
              <input type="date" value={counterData.proposed_date} onChange={e => setCounterData({ ...counterData, proposed_date: e.target.value })} className="w-full px-4 py-2.5 border border-[#e4e1db] rounded-[14px] text-[14px] focus:border-[#0e0e0e] focus:outline-none focus:ring-[3px] focus:ring-[rgba(14,14,14,0.06)]" />
            </div>
            <div>
              <label className="text-[12px] font-semibold text-[#0e0e0e] mb-1.5 block">Proposed Shoot Time</label>
              <select value={counterData.proposed_time} onChange={e => setCounterData({ ...counterData, proposed_time: e.target.value })} className="w-full px-4 py-2.5 border border-[#e4e1db] rounded-[14px] text-[14px] focus:border-[#0e0e0e] focus:outline-none bg-white">
                {['06:00','07:00','08:00','09:00','10:00','11:00','12:00','13:00','14:00','15:00','16:00','17:00','18:00','19:00','20:00','21:00','22:00','23:00','00:00'].map(t => <option key={t} value={t}>{t}</option>)}
              </select>
            </div>
            <div>
              <label className="text-[12px] font-semibold text-[#0e0e0e] mb-1.5 block">Proposed Delivery Date</label>
              <input type="date" value={counterData.proposed_delivery_date} onChange={e => setCounterData({ ...counterData, proposed_delivery_date: e.target.value })} className="w-full px-4 py-2.5 border border-[#e4e1db] rounded-[14px] text-[14px] focus:border-[#0e0e0e] focus:outline-none focus:ring-[3px] focus:ring-[rgba(14,14,14,0.06)]" />
            </div>
            <div>
              <label className="text-[12px] font-semibold text-[#0e0e0e] mb-1.5 block">Note (optional)</label>
              <Textarea value={counterData.note} onChange={e => setCounterData({ ...counterData, note: e.target.value })} placeholder="Explain your proposed changes..." className="border-[#e4e1db] focus:border-[#0e0e0e] rounded-[14px]" rows={3} />
            </div>
            <button onClick={handleCounterPropose} disabled={actionLoading} className="w-full text-[13px] font-medium py-2.5 rounded-full bg-[#0e0e0e] text-white hover:opacity-85 transition-opacity">
              Send Proposal
            </button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Review Dialog */}
      <Dialog open={reviewOpen} onOpenChange={setReviewOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle className="font-display font-normal text-[24px]">Leave a Review</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 mt-4">
            <div>
              <label className="text-[12px] font-semibold text-[#0e0e0e] mb-2 block">Rating</label>
              <div className="flex gap-2">
                {[1, 2, 3, 4, 5].map(star => (
                  <button key={star} type="button" onClick={() => setReviewData({ ...reviewData, rating: star })}>
                    <Star className={`w-8 h-8 transition-colors ${star <= reviewData.rating ? 'fill-[#d4a843] text-[#d4a843]' : 'text-[#e4e1db] hover:text-[#d4a843]'}`} />
                  </button>
                ))}
              </div>
            </div>
            <div>
              <label className="text-[12px] font-semibold text-[#0e0e0e] mb-2 block">Comment</label>
              <Textarea value={reviewData.comment} onChange={e => setReviewData({ ...reviewData, comment: e.target.value })} placeholder="Share your experience..." rows={4} className="border-[#e4e1db] focus:border-[#0e0e0e] rounded-[14px]" data-testid="review-comment-input" />
            </div>
            <button onClick={submitReview} disabled={actionLoading} className="w-full text-[13px] font-medium py-2.5 rounded-full bg-[#0e0e0e] text-white hover:opacity-85 transition-opacity" data-testid="submit-review-btn">
              {actionLoading ? 'Submitting...' : 'Submit Review'}
            </button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};

const CalendarIcon = () => (
  <svg width="40" height="40" viewBox="0 0 20 20" fill="none" stroke="currentColor" strokeWidth="1">
    <rect x="2" y="3" width="16" height="15" rx="2"/><path d="M2 8h16M6 1v4M14 1v4"/>
  </svg>
);

const EmptyState = ({ icon, title }) => (
  <div className="bg-white border border-[#e4e1db] rounded-[24px] flex flex-col items-center justify-center py-14 px-6 text-center">
    <div className="opacity-20 mb-4">{icon}</div>
    <p className="text-[15px] font-semibold text-[#0e0e0e]">{title}</p>
  </div>
);

export default Bookings;
